<template>
    <div id="main-page">
        <div class="container">
          <app-bar
            title="Event Saya"
            previous-link="/profile" />

          <!-- Family member filter dropdown -->
          <div class="family-filter mb-3">
            <div
              class="family-filter__trigger"
              :class="{ open: dropdownOpen }"
              @click="dropdownOpen = !dropdownOpen">
              <div class="family-filter__avatar" :style="selectedPeserta ? avatarStyle(selectedPeserta.nama_peserta) : {}">
                <span v-if="selectedPeserta">{{ initials(selectedPeserta.nama_peserta) }}</span>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div class="family-filter__label">
                <span class="family-filter__name">{{ selectedPeserta ? selectedPeserta.nama_peserta : 'Semua Anggota' }}</span>
                <span v-if="selectedPeserta" class="family-filter__hubungan">{{ selectedPeserta.hubungan }}</span>
              </div>
              <div class="family-filter__chevron" :class="{ rotated: dropdownOpen }">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>

            <transition name="dropdown">
              <div v-if="dropdownOpen" class="family-filter__panel">
                <div
                  class="family-filter__option"
                  :class="{ active: !selectedPeserta }"
                  @click="selectPeserta(null)">
                  <div class="family-filter__avatar family-filter__avatar--all">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <div class="family-filter__option-info">
                    <span class="family-filter__option-name">Semua Anggota</span>
                    <span class="family-filter__option-sub">Tampilkan semua event</span>
                  </div>
                  <div v-if="!selectedPeserta" class="family-filter__check">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                </div>

                <div v-if="loading.getPesertaEvent" class="family-filter__loading">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <span class="ms-2 fs-13 color-neutral-600">Memuat data...</span>
                </div>

                <div
                  v-for="peserta in eventPeserta"
                  :key="peserta.pas_id"
                  class="family-filter__option"
                  :class="{ active: selectedPeserta && selectedPeserta.pas_id === peserta.pas_id }"
                  @click="selectPeserta(peserta)">
                  <div class="family-filter__avatar" :style="avatarStyle(peserta.nama_peserta)">
                    {{ initials(peserta.nama_peserta) }}
                  </div>
                  <div class="family-filter__option-info">
                    <span class="family-filter__option-name">{{ peserta.nama_peserta }}</span>
                    <span class="family-filter__option-sub">{{ peserta.hubungan }}</span>
                  </div>
                  <div v-if="selectedPeserta && selectedPeserta.pas_id === peserta.pas_id" class="family-filter__check">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Click outside overlay -->
          <div v-if="dropdownOpen" class="dropdown-overlay" @click="dropdownOpen = false"></div>

          <Loading v-if="loading.getMyEvents" />

          <div v-else-if="myEvents.length" class="events-container">
            <!-- Summary header -->
            <div class="events-summary d-flex align-items-center justify-content-between mb-3">
              <div>
                <p class="fm-poppins fw-600 fs-14 color-neutral-700 mb-0">
                  {{ myEvents.length }} Event Terdaftar
                </p>
              </div>
              <div class="d-flex gap-2">
                <span
                  v-for="tab in tabs"
                  :key="tab.key"
                  class="filter-chip"
                  :class="{ active: activeTab === tab.key }"
                  @click="activeTab = tab.key">
                  {{ tab.label }}
                </span>
              </div>
            </div>

            <!-- Event cards -->
            <div class="row">
              <div
                v-for="event in filteredEvents"
                :key="event.pendaftar_id"
                class="col-12 col-md-6 col-lg-4 mb-3">
                <div class="event-card" @click="goToDetail(event)" style="cursor: pointer;">
                  <!-- Image section with gradient overlay -->
                  <div class="event-card__image">
                    <img :src="event.gambar_url" :alt="event.judul">
                    <span class="price-tag" :class="event.is_gratis === 't' ? 'price-tag--free' : 'price-tag--paid'">
                      {{ event.is_gratis === 't' ? 'GRATIS' : formatRupiah(event.harga) }}
                    </span>
                    <!-- Status indicator dot -->
                    <span class="status-dot" :class="event.status === '1' ? 'status-dot--active' : 'status-dot--pending'"></span>
                  </div>

                  <!-- Content section -->
                  <div class="event-card__body">
                    <h5 class="event-card__title fm-poppins fw-700">
                      {{ event.judul }}
                    </h5>

                    <div v-if="event.nama_pasien" class="patient-name d-flex align-items-center gap-2 mb-2">
                      <div class="detail-icon detail-icon--small">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <span class="fm-inter fw-600 fs-13 color-neutral-700">{{ event.nama_pasien }}</span>
                    </div>

                    <div class="event-card__details">
                      <div class="detail-row" v-if="event.tgl_mulai">
                        <div class="detail-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        </div>
                        <span class="fm-inter fw-500 fs-13 color-neutral-600">{{ formatTanggal(event.tgl_mulai) }}</span>
                      </div>
                      <div class="detail-row" v-if="event.lokasi">
                        <div class="detail-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        </div>
                        <span class="fm-inter fw-500 fs-13 color-neutral-600">{{ event.lokasi }}</span>
                      </div>
                    </div>

                    <!-- Divider -->
                    <div class="event-card__divider"></div>

                    <!-- Status + payment row -->
                    <div class="event-card__footer">
                      <div class="d-flex align-items-center gap-2 flex-wrap">
                        <span class="status-badge" :class="statusBadgeClass(event)">
                          <span class="status-badge__dot"></span>
                          {{ statusLabel(event) }}
                        </span>
                        <span
                          v-if="event.is_gratis === 'f'"
                          class="status-badge"
                          :class="event.status_bayar === '1' ? 'status-badge--lunas' : 'status-badge--belum-bayar'">
                          <span class="status-badge__dot"></span>
                          {{ event.status_bayar === '1' ? 'Lunas' : 'Belum Bayar' }}
                        </span>
                      </div>

                      <!-- Check-in/out timeline -->
                      <div v-if="event.check_in_at || event.check_out_at" class="checkin-timeline mt-2">
                        <div v-if="event.check_in_at" class="checkin-item">
                          <div class="checkin-marker checkin-marker--in"></div>
                          <div>
                            <span class="fm-inter fw-600 fs-11 color-neutral-700">Check-in</span>
                            <span class="fm-inter fw-400 fs-11 color-neutral-500 ms-1">{{ formatTanggal(event.check_in_at) }}</span>
                          </div>
                        </div>
                        <div v-if="event.check_out_at" class="checkin-item">
                          <div class="checkin-marker checkin-marker--out"></div>
                          <div>
                            <span class="fm-inter fw-600 fs-11 color-neutral-700">Check-out</span>
                            <span class="fm-inter fw-400 fs-11 color-neutral-500 ms-1">{{ formatTanggal(event.check_out_at) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty filtered state -->
            <div v-if="!filteredEvents.length" class="text-center py-4">
              <p class="fm-inter fw-500 fs-14 color-neutral-500 mb-0">Tidak ada event dengan filter ini.</p>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else
            class="empty-state d-flex flex-column align-items-center text-center pb-5 pt-5">
            <div class="empty-state__icon mb-3">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <line x1="9" y1="16" x2="15" y2="16"/>
              </svg>
            </div>
            <p class="fm-poppins fst-normal fw-600 fs-18 color-neutral-900 mb-1">Belum Ada Event</p>
            <p class="fm-nunito fst-normal fw-400 fs-14 color-neutral-500 mb-4" style="max-width: 260px;">
              Anda belum terdaftar di event manapun. Jelajahi event kesehatan terbaru kami.
            </p>
            <router-link to="/event" class="btn-explore">
              Lihat Event
            </router-link>
          </div>
        </div>
    </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import { nextTick, onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { title } from '@/core/page'
import Loading from '@/components/utility/Loading'
import { getMyEvents, getPesertaEvent, myEvents, eventPeserta, loading, regenerateMidtrans } from '@/core/event'
import { showNotif } from '@/core/utility'

const AVATAR_COLORS = [
  ['#007A98', '#E0F7FA'],
  ['#1CB242', '#E8F5E9'],
  ['#7B5EA7', '#EDE7F6'],
  ['#E67E22', '#FFF3E0'],
  ['#E53935', '#FFEBEE'],
  ['#0288D1', '#E1F5FE'],
  ['#00897B', '#E0F2F1']
]

export default {
  components: {
    AppBar, Loading
  },
  setup () {
    const router = useRouter()
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

    const activeTab = ref('semua')
    const tabs = [
      { key: 'semua', label: 'Semua' },
      { key: 'aktif', label: 'Aktif' },
      { key: 'selesai', label: 'Selesai' }
    ]

    const dropdownOpen = ref(false)
    const selectedPeserta = ref(null)

    const initials = (name) => {
      if (!name) return '?'
      return name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('')
    }

    const avatarStyle = (name) => {
      if (!name) return {}
      const idx = name.charCodeAt(0) % AVATAR_COLORS.length
      const [color, bg] = AVATAR_COLORS[idx]
      return { background: bg, color }
    }

    const selectPeserta = async (peserta) => {
      selectedPeserta.value = peserta
      dropdownOpen.value = false
      await getMyEvents(peserta ? peserta.pas_id : null)
    }

    const filteredEvents = computed(() => {
      if (activeTab.value === 'semua') return myEvents.value
      if (activeTab.value === 'aktif') {
        return myEvents.value.filter(e => e.status === '1' && !e.check_out_at)
      }
      return myEvents.value.filter(e => !!e.check_out_at)
    })

    const formatTanggal = (tgl) => {
      if (!tgl) return ''
      const date = new Date(tgl.replace(' ', 'T'))
      const day = date.getDate()
      const month = months[date.getMonth()]
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day} ${month} ${year} · ${hours}:${minutes} WIB`
    }

    const formatRupiah = (value) => {
      const num = parseInt(value) || 0
      return 'Rp ' + num.toLocaleString('id-ID')
    }

    const statusLabel = (event) => {
      return event.status === '1' ? 'Terdaftar' : 'Pending'
    }

    const statusBadgeClass = (event) => {
      return event.status === '1' ? 'status-badge--terdaftar' : 'status-badge--pending'
    }

    const goToDetail = (event) => {
      const isUnpaid = event.is_gratis === 'f' && event.status_bayar !== '1'
      if (isUnpaid && event.midtrans && event.midtrans.token) {
        const created = new Date(event.tgl_daftar)
        const isExpired = (Date.now() - created.getTime()) > 30 * 60 * 1000

        if (isExpired) {
          regenerateMidtrans(event.pendaftar_id).then(result => {
            if (!result.status) return
            window.getSnap().then(snap => {
              snap.pay(result.data.midtrans.token, {
                onSuccess: () => {
                  router.push({
                    name: 'MyEventDetail',
                    query: {
                      acara_id: event.acara_id || event.hai_acara_id || event.id,
                      pas_id_periksa: event.pas_id
                    }
                  })
                }
              })
            })
          })
          return
        }
        window.getSnap().then(snap => {
          snap.pay(event.midtrans.token, {
            onSuccess: () => {
              router.push({
                name: 'MyEventDetail',
                query: {
                  acara_id: event.acara_id || event.hai_acara_id || event.id,
                  pas_id_periksa: event.pas_id
                }
              })
            }
          })
        })
        return
      }
      router.push({
        name: 'MyEventDetail',
        query: {
          acara_id: event.acara_id || event.hai_acara_id || event.id,
          pas_id_periksa: event.pas_id
        }
      })
    }

    onMounted(async () => {
      await Promise.all([getMyEvents(), getPesertaEvent()])

      nextTick(() => {
        title.value = 'Event Saya'
      })
    })

    return {
      loading,
      myEvents,
      eventPeserta,
      activeTab,
      tabs,
      filteredEvents,
      dropdownOpen,
      selectedPeserta,
      initials,
      avatarStyle,
      selectPeserta,
      formatTanggal,
      formatRupiah,
      statusLabel,
      statusBadgeClass,
      goToDetail
    }
  }
}
</script>

<style lang="scss" scoped>
$primary: #007A98;
$primary-light: #EBFFFD;
$primary-dark: #005F82;
$green: #1CB242;
$green-light: #e8f8ec;
$orange: #FF9D0A;
$orange-light: #fff5e6;
$red: #FD5252;
$red-light: #fef0f0;
$blue: #3566FC;
$blue-light: #eef2ff;
$neutral-400: #CBD5E0;
$neutral-500: #A0AEC0;
$neutral-600: #718096;

.events-container {
  padding-top: 8px;
  padding-bottom: 24px;
}

.events-summary {
  padding: 0 2px;

  @media (max-width: 520px) {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px;
  }
}

.filter-chip {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 20px;
  background: #f1f5f9;
  color: $neutral-600;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: $primary-light;
    color: $primary;
  }

  &.active {
    background: $primary;
    color: #fff;
  }
}

// Event card
.event-card {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
  }

  &__image {
    position: relative;
    height: 210px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: contain;
    }
  }

  &__body {
    padding: 16px 18px 14px;

    @media (max-width: 520px) {
      padding: 14px 14px 12px;
    }
  }

  &__title {
    font-size: 17px;
    line-height: 24px;
    color: #1A202C;
    margin-bottom: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: 520px) {
      font-size: 16px;
      line-height: 22px;
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__divider {
    height: 1px;
    background: #f1f5f9;
    margin: 12px 0;
  }

  &__footer {
    display: flex;
    flex-direction: column;
  }
}

// Detail rows (date, location)
.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: $primary-light;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    stroke: $primary;
  }

  &--small {
    width: 24px;
    height: 24px;
    border-radius: 6px;
  }
}

// Price tag
.price-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 20px;
  line-height: 1.3;
  letter-spacing: 0.3px;
  backdrop-filter: blur(4px);
  z-index: 1;

  &--free {
    background: rgba($green, 0.9);
    color: #fff;
  }

  &--paid {
    background: rgba(255, 255, 255, 0.92);
    color: $primary;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

// Status dot on image
.status-dot {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  z-index: 1;

  &--active {
    background: $green;
    box-shadow: 0 0 0 3px rgba($green, 0.25);
  }

  &--pending {
    background: $orange;
    box-shadow: 0 0 0 3px rgba($orange, 0.25);
  }
}

// Status badges
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  line-height: 1.4;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &--terdaftar {
    background: $green-light;
    color: darken($green, 10%);

    .status-badge__dot {
      background: $green;
    }
  }

  &--pending {
    background: $orange-light;
    color: darken($orange, 10%);

    .status-badge__dot {
      background: $orange;
    }
  }

  &--lunas {
    background: $blue-light;
    color: darken($blue, 5%);

    .status-badge__dot {
      background: $blue;
    }
  }

  &--belum-bayar {
    background: $red-light;
    color: darken($red, 5%);

    .status-badge__dot {
      background: $red;
    }
  }
}

// Check-in timeline
.checkin-timeline {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 10px;
}

.checkin-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkin-marker {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &--in {
    background: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.15);
  }

  &--out {
    background: $neutral-500;
    box-shadow: 0 0 0 3px rgba($neutral-500, 0.15);
  }
}

// Dropdown overlay
.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: 99;
}

// Family filter
.family-filter {
  position: relative;
  z-index: 100;

  &__trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    user-select: none;

    &:hover {
      border-color: $primary;
    }

    &.open {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba($primary, 0.1);
    }
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #edf2f7;
    color: $neutral-600;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    letter-spacing: 0.5px;

    &--all {
      background: #edf2f7;
      color: $neutral-600;
    }
  }

  &__label {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #1A202C;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__hubungan {
    font-size: 11px;
    font-weight: 500;
    color: $neutral-500;
    line-height: 1.3;
    margin-top: 1px;
  }

  &__chevron {
    color: $neutral-500;
    flex-shrink: 0;
    transition: transform 0.2s ease;

    &.rotated {
      transform: rotate(180deg);
    }
  }

  &__panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    max-height: 300px;
    overflow-y: auto;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 14px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:not(:last-child) {
      border-bottom: 1px solid #f7fafc;
    }

    &:hover {
      background: #f8fafc;
    }

    &.active {
      background: $primary-light;
    }
  }

  &__option-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__option-name {
    font-size: 13px;
    font-weight: 600;
    color: #1A202C;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__option-sub {
    font-size: 11px;
    font-weight: 500;
    color: $neutral-500;
    line-height: 1.3;
    margin-top: 1px;
  }

  &__check {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: $primary;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__loading {
    display: flex;
    align-items: center;
    padding: 14px;
  }
}

// Dropdown transition
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

// Empty state
.empty-state {
  padding-top: 60px;

  &__icon {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: #f7fafc;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.btn-explore {
  display: inline-block;
  padding: 10px 28px;
  background: $primary;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 25px;
  text-decoration: none;
  transition: background 0.2s ease;

  &:hover {
    background: $primary-dark;
    color: #fff;
  }
}
</style>
